/**
 * MattressArt — procedural SVG product illustrations.
 *
 * Every SKU gets a distinctive side-view CUTAWAY drawing driven by its real
 * game specs, like actual mattress marketing cross-sections:
 *   • archetype  → internal layers (foam / coils / gel swirls / latex pores /
 *                  pillow-top luxury stack)
 *   • quality    → stitch density + gold piping at high spec
 *   • coolingScore → frost motifs on the cover
 *   • seed       → deterministic decorative jitter so SKUs feel individual
 */

import { memo, useId } from 'react';
import { hash32, mulberry32 } from '@/game/rng';
import type { ArchetypeId } from '@/game/types';

export interface MattressArtProps {
  archetype: ArchetypeId;
  quality: number;
  coolingScore: number;
  seed?: string | number;
  className?: string;
}

const PALETTES: Record<ArchetypeId, { fabricA: string; fabricB: string; quilt: string }> = {
  allFoam: { fabricA: '#64748b', fabricB: '#475569', quilt: '#cbd5e1' },
  hybrid: { fabricA: '#6d28d9', fabricB: '#4c1d95', quilt: '#ddd6fe' },
  coolingHybrid: { fabricA: '#0284c7', fabricB: '#075985', quilt: '#bae6fd' },
  latex: { fabricA: '#4d7c0f', fabricB: '#3f6212', quilt: '#ecfccb' },
  luxury: { fabricA: '#1e293b', fabricB: '#0f172a', quilt: '#e2e8f0' },
};

function Snowflake({ x, y, s }: { x: number; y: number; s: number }) {
  return (
    <g stroke="#e0f2fe" strokeWidth={1.4} strokeLinecap="round">
      <line x1={x - s} y1={y} x2={x + s} y2={y} />
      <line x1={x} y1={y - s} x2={x} y2={y + s} />
      <line x1={x - s * 0.7} y1={y - s * 0.7} x2={x + s * 0.7} y2={y + s * 0.7} />
      <line x1={x - s * 0.7} y1={y + s * 0.7} x2={x + s * 0.7} y2={y - s * 0.7} />
    </g>
  );
}

function Coil({ x, y }: { x: number; y: number }) {
  // Stylized side-view spring: hourglass with a waist dot.
  return (
    <g>
      <path d={`M ${x - 4} ${y - 7} Q ${x + 4} ${y} ${x - 4} ${y + 7}`} fill="none" stroke="#fb7185" strokeWidth={1.6} />
      <path d={`M ${x + 4} ${y - 7} Q ${x - 4} ${y} ${x + 4} ${y + 7}`} fill="none" stroke="#f43f5e" strokeWidth={1.6} />
    </g>
  );
}

function MattressArtInner({ archetype, quality, coolingScore, seed = 0, className }: MattressArtProps) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const p = PALETTES[archetype];
  const rng = mulberry32(hash32(typeof seed === 'string' ? [...seed].reduce((a, c) => a + c.charCodeAt(0), 0) : seed));
  const jitter = (n: number) => (rng() - 0.5) * n;
  const goldPiping = quality >= 78;
  const stitches = quality >= 55 ? 11 : 8;

  return (
    <svg viewBox="0 0 200 150" className={className} role="img" aria-label={`${archetype} mattress illustration`}>
      <defs>
        <radialGradient id={`${uid}-bg`} cx="50%" cy="42%" r="65%">
          <stop offset="0%" stopColor="#1d4ed8" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-fab`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={p.fabricA} />
          <stop offset="100%" stopColor={p.fabricB} />
        </linearGradient>
      </defs>

      {/* Backdrop + shadow */}
      <circle cx="100" cy="66" r="62" fill={`url(#${uid}-bg)`} />
      <ellipse cx="100" cy="126" rx="74" ry="7" fill="#000" opacity="0.14" />

      {/* Luxury pillow-top bump sits above the body */}
      {archetype === 'luxury' && (
        <rect x={26} y={30} width={148} height={18} rx={9} fill="#fef3c7" stroke="#d4af37" strokeWidth={1} />
      )}

      {/* Body */}
      <rect
        x={18}
        y={archetype === 'luxury' ? 46 : 44}
        width={164}
        height={64}
        rx={10}
        fill={`url(#${uid}-fab)`}
        stroke={goldPiping ? '#fbbf24' : p.fabricB}
        strokeWidth={goldPiping ? 2.2 : 1.4}
      />

      {/* Quilted cover strip */}
      <rect x={20} y={archetype === 'luxury' ? 48 : 46} width={160} height={12} rx={6} fill={p.quilt} opacity="0.92" />
      {Array.from({ length: stitches }).map((_, i) => {
        const x = 26 + i * ((148 / (stitches - 1)) || 0);
        return <line key={i} x1={x} y1={49} x2={x - 3} y2={56} stroke={p.fabricB} strokeWidth={1} opacity={0.55} />;
      })}

      {/* Cutaway window showing the layers */}
      <rect x={34} y={62} width={132} height={40} rx={6} fill="#0f172a" opacity={0.25} />

      {archetype === 'allFoam' && (
        <>
          <rect x={36} y={64} width={128} height={15} rx={3} fill="#bfdbfe" />
          <rect x={36} y={81} width={128} height={19} rx={3} fill="#94a3b8" />
          <line x1={38} y1={71.5} x2={162} y2={71.5} stroke="#60a5fa" strokeWidth={1} strokeDasharray="6 4" />
        </>
      )}

      {archetype === 'hybrid' && (
        <>
          <rect x={36} y={64} width={128} height={10} rx={3} fill="#c7d2fe" />
          <rect x={36} y={76} width={128} height={13} rx={2} fill="#f1f5f9" />
          {Array.from({ length: 9 }).map((_, i) => (
            <Coil key={i} x={44 + i * 14 + jitter(2)} y={82.5} />
          ))}
          <rect x={36} y={91} width={128} height={11} rx={3} fill="#94a3b8" />
        </>
      )}

      {archetype === 'coolingHybrid' && (
        <>
          <rect x={36} y={64} width={128} height={9} rx={3} fill="#a5f3fc" />
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <circle key={i} cx={44 + i * 19 + jitter(4)} cy={68.5 + jitter(1.5)} r={2.6} fill="#22d3ee" opacity={0.85} />
          ))}
          <rect x={36} y={75} width={128} height={11} rx={2} fill="#f1f5f9" />
          {Array.from({ length: 9 }).map((_, i) => (
            <Coil key={i} x={44 + i * 14 + jitter(2)} y={80.5} />
          ))}
          <rect x={36} y={88} width={128} height={12} rx={3} fill="#94a3b8" />
        </>
      )}

      {archetype === 'latex' && (
        <>
          <rect x={36} y={64} width={128} height={24} rx={3} fill="#fef9c3" />
          {Array.from({ length: 5 }).map((_, r) =>
            Array.from({ length: 16 }).map((_, c) => (
              <circle key={`${r}-${c}`} cx={42 + c * 7.4 + jitter(1)} cy={69 + r * 4.6 + jitter(1)} r={1.15} fill="#a3a3a3" opacity={0.5} />
            )),
          )}
          <rect x={36} y={90} width={128} height={10} rx={3} fill="#a8a29e" />
        </>
      )}

      {archetype === 'luxury' && (
        <>
          <rect x={36} y={64} width={128} height={9} rx={3} fill="#fde68a" />
          <rect x={36} y={75} width={128} height={10} rx={3} fill="#bfdbfe" />
          <rect x={36} y={87} width={128} height={7} rx={2} fill="#f1f5f9" />
          {Array.from({ length: 9 }).map((_, i) => (
            <Coil key={i} x={44 + i * 14 + jitter(1)} y={90.5} />
          ))}
          <rect x={36} y={96} width={128} height={6} rx={2} fill="#64748b" />
        </>
      )}

      {/* Cooling frost motifs on the cover */}
      {coolingScore >= 65 && (
        <g>
          <Snowflake x={172} y={40} s={4.2} />
          {coolingScore >= 80 && <Snowflake x={158} y={33} s={3} />}
          {coolingScore >= 90 && <Snowflake x={182} y={28} s={2.6} />}
        </g>
      )}
    </svg>
  );
}

export const MattressArt = memo(MattressArtInner);
